import React from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

export class McpTokens extends React.PureComponent {
    state = {
        tokens: [],
        loading: true,
        error: null,
        newTokenName: '',
        generatedToken: null,
        serverName: null,
        showRevokeDialog: false,
        revokeTarget: null,
        copied: false,
    };

    componentDidMount() {
        this.loadTokens();
    }

    loadTokens = () => {
        this.setState({ loading: true });
        fetch('/api/mcp-tokens')
            .then(r => r.json())
            .then(data => {
                if (data.error) {
                    this.setState({ loading: false, error: data.error });
                } else {
                    this.setState({ loading: false, tokens: data.results || [] });
                }
            })
            .catch(err => this.setState({ loading: false, error: err.message }));
    }

    handleGenerate = () => {
        const name = this.state.newTokenName.trim() || 'Unnamed Token';
        fetch('/api/mcp-tokens', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name }),
        })
            .then(r => r.json())
            .then(data => {
                if (data.error) {
                    this.setState({ error: data.error });
                } else {
                    this.setState({
                        generatedToken: data.result.token,
                        serverName: data.result.server_name || 'support',
                        newTokenName: '',
                        copied: false,
                    });
                    this.loadTokens();
                }
            });
    }

    handleCopy = () => {
        if (this.state.generatedToken) {
            navigator.clipboard.writeText(this.state.generatedToken);
            this.setState({ copied: true });
        }
    }

    // Single source for the config, so the displayed block and the copy
    // button can never disagree.
    mcpConfigText = () => JSON.stringify({
        servers: {
            [this.state.serverName || 'support']: {
                type: 'http',
                url: `${window.location.origin}/mcp`,
                headers: {
                    Authorization: `Bearer ${this.state.generatedToken}`,
                },
            },
        },
    }, null, 2)

    handleCopyConfig = () => {
        navigator.clipboard.writeText(this.mcpConfigText());
    }

    showRevokeDialog = (token) => {
        this.setState({ showRevokeDialog: true, revokeTarget: token });
    }

    closeRevokeDialog = () => {
        this.setState({ showRevokeDialog: false, revokeTarget: null });
    }

    handleRevoke = () => {
        const tokenHash = this.state.revokeTarget._id;
        fetch(`/api/mcp-tokens/${tokenHash}`, { method: 'DELETE' })
            .then(r => r.json())
            .then(data => {
                if (data.error) {
                    this.setState({ error: data.error });
                } else {
                    this.setState({ showRevokeDialog: false, revokeTarget: null });
                    this.loadTokens();
                }
            });
    }

    dismissGeneratedToken = () => {
        this.setState({ generatedToken: null });
    }

    render() {
        const { tokens, loading, error, generatedToken, newTokenName, showRevokeDialog, revokeTarget, copied } = this.state;

        return (
            <div style={{ padding: 24, maxWidth: 900 }}>
                <h2>MCP API Tokens</h2>
                <p style={{ color: '#666' }}>
                    Generate tokens to connect AI agents (GitHub Copilot, Claude, etc.) to this support service via MCP.
                    Each token grants read access to the game database this instance is connected to.
                </p>

                {error && <p style={{ color: 'red' }}>{JSON.stringify(error)}</p>}

                {/* Generate new token */}
                <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <TextField
                        label="Token Name"
                        placeholder="e.g., My Laptop"
                        value={newTokenName}
                        onChange={e => this.setState({ newTokenName: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        style={{ width: 300 }}
                    />
                    <Button variant="contained" color="primary" onClick={this.handleGenerate}>
                        Generate New Token
                    </Button>
                </div>

                {/* Show generated token (once) */}
                {generatedToken && (
                    <div style={{ background: '#e8f5e9', border: '1px solid #4caf50', borderRadius: 4, padding: 16, marginBottom: 24 }}>
                        <p><strong>Token generated successfully!</strong> Copy it now — it will not be shown again.</p>
                        <code style={{ display: 'block', background: '#fff', padding: 8, borderRadius: 4, wordBreak: 'break-all', marginBottom: 8 }}>
                            {generatedToken}
                        </code>
                        <Button variant="outlined" size="small" onClick={this.handleCopy} style={{ marginRight: 8 }}>
                            {copied ? 'Copied!' : 'Copy Token'}
                        </Button>
                        <Button variant="outlined" size="small" onClick={this.handleCopyConfig} style={{ marginRight: 8 }}>
                            Copy VS Code MCP Config
                        </Button>
                        <Button size="small" onClick={this.dismissGeneratedToken}>
                            Dismiss
                        </Button>

                        <div style={{ marginTop: 12 }}>
                            <p style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                                <strong>VS Code Setup:</strong> Add to <code>.vscode/mcp.json</code> in your game repo (or VS Code User Settings):
                            </p>
                            <pre style={{ background: '#fff', padding: 8, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>
{this.mcpConfigText()}
                            </pre>
                        </div>
                    </div>
                )}

                {/* Existing tokens table */}
                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Last Used</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tokens.map(token => (
                                <TableRow key={token._id}>
                                    <TableCell>{token.name}</TableCell>
                                    <TableCell>{new Date(token.created).toLocaleString()}</TableCell>
                                    <TableCell>{token.last_used ? new Date(token.last_used).toLocaleString() : 'Never'}</TableCell>
                                    <TableCell>
                                        <Button size="small" color="secondary" onClick={() => this.showRevokeDialog(token)}>
                                            Revoke
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {tokens.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} style={{ textAlign: 'center', color: '#999' }}>
                                        No tokens yet. Generate one above to get started.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}

                {/* Revoke confirmation dialog */}
                <Dialog open={showRevokeDialog} onClose={this.closeRevokeDialog}>
                    <DialogTitle>Revoke Token</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to revoke token &ldquo;{revokeTarget && revokeTarget.name}&rdquo;?
                            Any MCP connections using this token will stop working immediately.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeRevokeDialog}>Cancel</Button>
                        <Button onClick={this.handleRevoke} color="secondary">Revoke</Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}
